"use client";

import { useCallback, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { randomId } from "@/lib/random-id";
import { handleError } from "@/lib/handle-error";
import { gradingPhase, type GradingFeedbackAccess } from "./grading-session";

interface GradingAttempt {
  sessionId: string;
  feedbackAccess: GradingFeedbackAccess | null;
}

interface GradingRequest {
  photoStorageIds: Array<Id<"_storage">>;
  userProductTypeSlug: string | undefined;
  sourcePath: string;
}

function startFailureMessage(error: unknown): string | null {
  const handled = handleError(error, { display: "silent" });
  if (handled.action === "ignore") return null;
  return handled.message;
}

function useStartGrading() {
  const [attempt, setAttempt] = useState<GradingAttempt | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const createSession = useMutation(
    api.ai.grading.mutations.createGradingSession,
  );

  const start = useCallback(
    async (request: GradingRequest) => {
      const sessionId = randomId();
      const feedbackToken = randomId();
      const keepIfCurrent =
        (next: GradingAttempt | null) => (current: GradingAttempt | null) =>
          current?.sessionId === sessionId ? next : current;
      setStartError(null);
      setAttempt({ sessionId, feedbackAccess: null });
      try {
        const gradingSessionId = await createSession({
          sessionId,
          feedbackToken,
          ...request,
        });
        const feedbackAccess = { gradingSessionId, feedbackToken };
        setAttempt(keepIfCurrent({ sessionId, feedbackAccess }));
      } catch (error) {
        setAttempt(keepIfCurrent(null));
        setStartError(startFailureMessage(error));
      }
    },
    [createSession],
  );

  return { attempt, setAttempt, startError, setStartError, start };
}

export function useGradingSession() {
  const { attempt, setAttempt, startError, setStartError, start } =
    useStartGrading();
  const session = useQuery(
    api.ai.grading.publicQueries.getSessionById,
    attempt ? { sessionId: attempt.sessionId } : "skip",
  );
  const settledStatus = attempt?.feedbackAccess ? session?.status : undefined;

  const reset = useCallback(() => {
    setAttempt(null);
    setStartError(null);
  }, [setAttempt, setStartError]);

  return {
    phase: gradingPhase(attempt !== null, settledStatus),
    session,
    attempt,
    startError,
    dismissStartError: () => setStartError(null),
    start,
    reset,
  };
}

export type GradingSessionState = ReturnType<typeof useGradingSession>;
